import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Alert, Button, Form, Modal, Spinner, Table } from "react-bootstrap";
import API from "../../services/api";
import "../EmployeeAccounts.css";

const fmtDate = (value) => value ? new Date(value).toLocaleString("en-IN") : "-";
const money = (value) => `INR ${Number(value || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const statusText = (value = "") => String(value || "pending").replace(/-/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());

export default function PlantSupplierQualityWorkflow() {
  const [rows, setRows] = useState([]);
  const [qualityUsers, setQualityUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [message, setMessage] = useState("");
  const [assignRow, setAssignRow] = useState(null);
  const [decisionRow, setDecisionRow] = useState(null);
  const [unloadingRow, setUnloadingRow] = useState(null);
  const [unloadingMode, setUnloadingMode] = useState("start");
  const [assignForm, setAssignForm] = useState({ qualityEmployeeId: "", remarks: "" });
  const [decisionForm, setDecisionForm] = useState({ decision: "approved", remarks: "", queryText: "", escalateTo: "admin" });
  const [unloadingForm, setUnloadingForm] = useState({ warehouseLocation: "", tareWeight: "", tareSlip: "", shortWeightDeduction: "", otherDeduction: "", taxDeduction: "", emptyVehiclePhoto: "", remarks: "" });

  const load = async () => {
    setLoading(true);
    setErr("");
    try {
      const [rowsRes, usersRes] = await Promise.all([
        API.get("/employee/plant/supplier-quality-dispatches"),
        API.get("/employee/plant/quality-employees"),
      ]);
      setRows(Array.isArray(rowsRes.data) ? rowsRes.data : []);
      setQualityUsers(Array.isArray(usersRes.data) ? usersRes.data : []);
    } catch (e) {
      setErr(e?.response?.data?.message || "Failed to load supplier quality workflow.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openAssign = (row) => {
    setAssignRow(row);
    setAssignForm({
      qualityEmployeeId: row.plantWorkflow?.qualityAssignedTo?._id || "",
      remarks: row.plantWorkflow?.plantRemarks || "",
    });
  };

  const saveAssign = async (e) => {
    e.preventDefault();
    if (!assignRow?._id) return;
    try {
      setSaving(true);
      await API.patch(`/employee/plant/supplier-quality-dispatches/${assignRow._id}/assign-quality`, assignForm);
      setAssignRow(null);
      setMessage("Dispatch assigned to quality.");
      load();
    } catch (error) {
      setErr(error?.response?.data?.message || "Assign failed");
    } finally {
      setSaving(false);
    }
  };

  const openDecision = (row) => {
    setDecisionRow(row);
    setDecisionForm({
      decision: row.plantWorkflow?.plantDecision === "pending" ? "approved" : row.plantWorkflow?.plantDecision || "approved",
      remarks: row.plantWorkflow?.plantRemarks || "",
      queryText: row.plantWorkflow?.queryText || "",
      escalateTo: row.plantWorkflow?.queryEscalatedTo === "hr" ? "hr" : "admin",
    });
  };

  const saveDecision = async (e) => {
    e.preventDefault();
    if (!decisionRow?._id) return;
    try {
      setSaving(true);
      await API.patch(`/employee/plant/supplier-quality-dispatches/${decisionRow._id}/decision`, decisionForm);
      setDecisionRow(null);
      setMessage("Plant decision saved.");
      load();
    } catch (error) {
      setErr(error?.response?.data?.message || "Decision failed");
    } finally {
      setSaving(false);
    }
  };

  const openUnloading = (row, mode) => {
    setUnloadingRow(row);
    setUnloadingMode(mode);
    setUnloadingForm({ warehouseLocation: row.unloading?.warehouseLocation || "", tareWeight: row.weighbridge?.tareWeight || "", tareSlip: "", shortWeightDeduction: "", otherDeduction: "", taxDeduction: "", emptyVehiclePhoto: "", remarks: "" });
  };

  const saveUnloading = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      await API.patch(`/employee/plant/supplier-quality-dispatches/${unloadingRow._id}/unloading/${unloadingMode}`, unloadingForm);
      setUnloadingRow(null);
      setMessage(unloadingMode === "start" ? "Unloading started." : "Unloading completed and GRN generated.");
      load();
    } catch (error) { setErr(error?.response?.data?.message || "Unloading update failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="accounts-page">
      <div className="accounts-header">
        <div>
          <div className="accounts-title-row">
            <h4 className="mb-0">Supplier Quality Workflow</h4>
            <span className="accounts-count">{rows.length} dispatches</span>
          </div>
          <p className="accounts-subtitle">Gate entry ke baad quality ko assign karein, report review karein, supplier/accounts handoff complete karein.</p>
        </div>
        <div className="accounts-actions">
          <Button variant="outline-secondary" size="sm" onClick={load}>Refresh</Button>
        </div>
      </div>

      {err && <Alert variant="danger">{err}</Alert>}
      {message && <Alert variant="success">{message}</Alert>}

      {loading ? (
        <div className="d-flex justify-content-center py-5"><Spinner animation="border" /></div>
      ) : (
        <div className="accounts-table-card">
          <Table hover responsive className="accounts-table">
            <thead>
              <tr>
                <th>Supplier / Vehicle</th>
                <th>Material</th>
                <th>Gate / Workflow</th>
                <th>Quality Assigned</th>
                <th>Quality Result</th>
                <th>Amount</th>
                <th>Supplier</th>
                <th className="text-end">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan="8" className="text-center text-muted">No supplier dispatches found.</td></tr>
              ) : rows.map((row) => (
                <tr key={row._id}>
                  <td>
                    {row.supplierUser?._id ? (
                      <Link to={`/employee/suppliers/${row.supplierUser._id}`} className="fw-bold text-decoration-none">
                        {row.companyName || row.supplierName || "-"}
                      </Link>
                    ) : (
                      <strong>{row.companyName || row.supplierName || "-"}</strong>
                    )}
                    <div className="text-muted small">{row.vehicle?.number || "-"} | {fmtDate(row.plantWorkflow?.gateEnteredAt || row.submittedAt)}</div>
                  </td>
                  <td>{row.materialName || row.productName || "-"}<div className="text-muted small">{row.quantity || "-"} {row.unit || ""}</div></td>
                  <td>{statusText(row.plantWorkflow?.status || row.approvalStatus)}</td>
                  <td>{row.plantWorkflow?.qualityAssignedTo?.fullName || "-"}</td>
                  <td>{statusText(row.qualityInspection?.status)}<div className="text-muted small">{row.qualityInspection?.reportNo || ""}</div></td>
                  <td>{money(row.qualityInspection?.proposedAmount)}</td>
                  <td>{statusText(row.plantWorkflow?.supplierResponse)}</td>
                  <td className="text-end">
                    <div className="accounts-row-actions">
                      <Button size="sm" variant="outline-primary" onClick={() => openAssign(row)}>Assign</Button>
                      <Button size="sm" variant="primary" onClick={() => openDecision(row)}>Decision</Button>
                      {row.plantWorkflow?.status === "unloading-ready" && <Button size="sm" variant="success" onClick={() => openUnloading(row, "start")}>Start Unloading</Button>}
                      {row.plantWorkflow?.status === "unloading-started" && <Button size="sm" variant="warning" onClick={() => openUnloading(row, "complete")}>Complete + GRN</Button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}

      <Modal show={!!assignRow} onHide={() => setAssignRow(null)} centered>
        <Modal.Header closeButton><Modal.Title>Assign Quality Check</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form onSubmit={saveAssign}>
            <Form.Group className="mb-3">
              <Form.Label>Quality Employee</Form.Label>
              <Form.Select value={assignForm.qualityEmployeeId} onChange={(e) => setAssignForm({ ...assignForm, qualityEmployeeId: e.target.value })} required>
                <option value="">Select employee</option>
                {qualityUsers.map((user) => (
                  <option key={user._id} value={user._id}>{user.fullName || user.email}</option>
                ))}
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Instruction / Remarks</Form.Label>
              <Form.Control as="textarea" rows={3} value={assignForm.remarks} onChange={(e) => setAssignForm({ ...assignForm, remarks: e.target.value })} />
            </Form.Group>
            <div className="d-flex justify-content-end gap-2">
              <Button variant="outline-secondary" onClick={() => setAssignRow(null)}>Cancel</Button>
              <Button type="submit" disabled={saving}>Assign</Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>

      <Modal show={!!decisionRow} onHide={() => setDecisionRow(null)} centered>
        <Modal.Header closeButton><Modal.Title>Plant Manager Decision</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form onSubmit={saveDecision}>
            <Alert variant="info">
              Quality amount: <strong>{money(decisionRow?.qualityInspection?.proposedAmount)}</strong>
            </Alert>
            <Form.Group className="mb-3">
              <Form.Label>Decision</Form.Label>
              <Form.Select value={decisionForm.decision} onChange={(e) => setDecisionForm({ ...decisionForm, decision: e.target.value })}>
                <option value="approved">Approve and send to Accounts</option>
                <option value="rejected">Quality mismatch - ask supplier agreement</option>
                <option value="query">Raise query</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Remarks</Form.Label>
              <Form.Control as="textarea" rows={3} value={decisionForm.remarks} onChange={(e) => setDecisionForm({ ...decisionForm, remarks: e.target.value })} />
            </Form.Group>
            {decisionForm.decision === "query" ? (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>Query Details</Form.Label>
                  <Form.Control as="textarea" rows={3} value={decisionForm.queryText} onChange={(e) => setDecisionForm({ ...decisionForm, queryText: e.target.value })} />
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Escalate To</Form.Label>
                  <Form.Select value={decisionForm.escalateTo} onChange={(e) => setDecisionForm({ ...decisionForm, escalateTo: e.target.value })}>
                    <option value="admin">Admin</option>
                    <option value="hr">HR</option>
                  </Form.Select>
                </Form.Group>
              </>
            ) : null}
            <div className="d-flex justify-content-end gap-2">
              <Button variant="outline-secondary" onClick={() => setDecisionRow(null)}>Cancel</Button>
              <Button type="submit" disabled={saving}>Save Decision</Button>
            </div>
          </Form>
        </Modal.Body>
      </Modal>
      <Modal show={!!unloadingRow} onHide={() => setUnloadingRow(null)} centered>
        <Modal.Header closeButton><Modal.Title>{unloadingMode === "start" ? "Start Unloading" : "Complete Unloading & Generate GRN"}</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form onSubmit={saveUnloading}>
            {unloadingMode === "start" ? (
              <Form.Group className="mb-3"><Form.Label>Warehouse / Bin Location</Form.Label><Form.Control value={unloadingForm.warehouseLocation} onChange={(e) => setUnloadingForm({ ...unloadingForm, warehouseLocation: e.target.value })} required /></Form.Group>
            ) : (
              <>
                <Form.Group className="mb-3"><Form.Label>Empty Vehicle / Tare Weight</Form.Label><Form.Control type="number" min="0" step="0.01" value={unloadingForm.tareWeight} onChange={(e) => setUnloadingForm({ ...unloadingForm, tareWeight: e.target.value })} required /></Form.Group>
                <Form.Group className="mb-3"><Form.Label>Tare Slip Number / URL</Form.Label><Form.Control value={unloadingForm.tareSlip} onChange={(e) => setUnloadingForm({ ...unloadingForm, tareSlip: e.target.value })} /></Form.Group>
                <Form.Group className="mb-3"><Form.Label>Short Weight Deduction</Form.Label><Form.Control type="number" min="0" value={unloadingForm.shortWeightDeduction} onChange={(e) => setUnloadingForm({ ...unloadingForm, shortWeightDeduction: e.target.value })} /></Form.Group>
                <Form.Group className="mb-3"><Form.Label>Other Deduction</Form.Label><Form.Control type="number" min="0" value={unloadingForm.otherDeduction} onChange={(e) => setUnloadingForm({ ...unloadingForm, otherDeduction: e.target.value })} /></Form.Group>
                <Form.Group className="mb-3"><Form.Label>TDS / Tax Deduction</Form.Label><Form.Control type="number" min="0" value={unloadingForm.taxDeduction} onChange={(e) => setUnloadingForm({ ...unloadingForm, taxDeduction: e.target.value })} /></Form.Group>
                <Form.Group className="mb-3"><Form.Label>Empty Vehicle Photo URL</Form.Label><Form.Control value={unloadingForm.emptyVehiclePhoto} onChange={(e) => setUnloadingForm({ ...unloadingForm, emptyVehiclePhoto: e.target.value })} /></Form.Group>
              </>
            )}
            <Form.Group className="mb-3"><Form.Label>Remarks</Form.Label><Form.Control as="textarea" rows={2} value={unloadingForm.remarks} onChange={(e) => setUnloadingForm({ ...unloadingForm, remarks: e.target.value })} /></Form.Group>
            <Button type="submit" disabled={saving}>{saving ? "Saving..." : "Save"}</Button>
          </Form>
        </Modal.Body>
      </Modal>
    </div>
  );
}
